/**
 * Annotation layers: coloured, see-through overlays painted onto the splat inside shapes the
 * creator draws on a surface (a façade, a roof slope, an interior wall), grouped into layers
 * that visitors switch on and off from the viewer's layers panel (e.g. "Building History" →
 * "18th century").
 *
 * Shapes are picked with the same shape picker as focus and haze regions (area-shape.ts). Most
 * are outlines clicked onto the splat: fitted to a plane (utils/surfacePolygon) and turned into a
 * slab, the outline extended `depth` in front of and behind that plane (outline-shape.ts).
 * Splats whose centres fall in the slab are tinted with their layer's colour, so the scan still
 * shows through, follows the surface relief, and stays hidden behind whatever is in front of it.
 * A shape can also be a sphere or box (a volume: a room, a tree) or whole splat objects (a tree
 * separated from the scan in Splat Edit, a splat model: `shape: 'splats'`, splat-objects.ts), and
 * any shape can apply outside itself instead (the layer colours everything except it).
 *
 * Shader cost model (same owner as focus / haze regions, see splat-region-effects.ts):
 * - Outlines are baked into the shader source as straight-line code with literal numbers (see
 *   the layout note in outline-shape.ts). Editing one recompiles, once.
 * - Sphere / box shapes take their transform from a uniform slot (`uAnnotationShape<k>`), like
 *   focus regions, so moving or resizing them never recompiles. There are
 *   MAX_ANNOTATION_REGION_SHAPES slots.
 * - Splat object shapes read the splat's object flags (no geometry at all); which objects a shape
 *   names is a uniform change. There are MAX_ANNOTATION_OBJECT_SHAPES of them.
 * - Each layer's colour and strength ride in one vec4 uniform (`uAnnotationGroup<i>`, rgb +
 *   strength), so switching layers, fading them and recolouring them never recompile. A layer
 *   at strength 0 skips its shapes entirely, so hidden layers cost nothing per splat.
 */
import {
    createRegionUniforms,
    normalizeRegion,
    regionUniformShader,
    regionWeight,
    writeRegionUniform,
    type RegionShape,
    type RegionVector,
} from './region-shape';
import { applyAreaShader, areaApply, combineAreaShader, combineAreaWeights, mirrorZ, type AreaApply, type AreaShapeKind } from './area-shape';
import {
    MAX_OUTLINE_COORDINATE,
    MAX_OUTLINE_POINTS,
    outlineShaderEdges,
    outlineSlabDefaults,
    outlineSlabShader,
    outlineSlabWeight,
    resolveOutlineSlab,
    type OutlineShaderEdge,
    type OutlineSlab,
} from './outline-shape';
import type { RegionColorEffect } from './splat-region-effects';
import { normalizeSplatObjectIds, splatObjectLaneCall } from './splat-objects';
import type { SceneTrigger } from '../types';
import type { SurfacePoint, SurfacePolygon } from '../utils/surfacePolygon';

export interface AnnotationPoint {
    x: number;
    y: number;
    z: number;
}

/** A layer visitors switch on and off, e.g. "18th century". */
export interface AnnotationLayerGroup {
    id: string;
    name: string;
    /** Overlay colour, #rrggbb. */
    color: string;
    /** How strongly the colour covers the splat, 0..1. Default 0.45. */
    opacity?: number;
    /** Shown when the scene opens. Default true. */
    visibleOnLoad?: boolean;
    /** Optional heading the layer is listed under in the panel (a sub-menu, e.g. "Façade"). */
    section?: string;
    /** Show the total area of the layer's shapes next to its name in the panel. */
    showArea?: boolean;
    /** Optional one-line note shown under the name in the panel. */
    description?: string;
    /**
     * Optional automatic switches (same kinds as overlay triggers): the layer turns on while a
     * waypoint / progress / camera-area condition holds, after a scene-start delay, or when a
     * hotspot is clicked (a click toggles it). Visitors can still switch it from the panel.
     */
    triggers?: SceneTrigger[];
}

/** A shape in one layer: an outline drawn on the splat surface (the default), a sphere / box, or whole splat objects. */
export interface AnnotationShape {
    id: string;
    groupId: string;
    name?: string;
    /** 'outline' (default; `points`), a 'sphere' / 'box' volume (position / rotation / size), or 'splats' (`splatIds`). */
    shape?: AreaShapeKind;
    /** 'splats' shapes: the splat objects the layer colours, by stable id (MAIN_SPLAT_OBJECT_ID or a custom mesh id). */
    splatIds?: string[];
    /**
     * The clicked outline, in order, in runtime scene coordinates: the same space as
     * `measurements` (NOT the Z-mirrored editor coordinates focus / haze regions use).
     * Empty for a sphere / box.
     */
    points: AnnotationPoint[];
    /** How far in front of and behind the surface splats are coloured, in scene units. Default from the outline size. Outlines only. */
    depth?: number;
    /**
     * Width of the soft edge. Outline: scene units, default from the outline size. Sphere / box:
     * a fraction of the half-size (0..1) like focus regions, default 0.25.
     */
    feather?: number;
    /** Sphere / box centre, in the editor's Z-mirrored scene coordinates (like focus / haze regions). */
    position?: RegionVector;
    /** Sphere / box rotation in degrees, editor convention. */
    rotation?: RegionVector;
    /** Sphere / box full size (a sphere with unequal sizes is an ellipsoid). */
    size?: RegionVector;
    /** 'outside' colours everything except the shape (within its layer). Default 'inside'. */
    apply?: AreaApply;
    /** False keeps the shape in the scene file without drawing it. */
    enabled?: boolean;
}

export interface AnnotationLayersPanelConfig {
    /** Show the layers button and panel in the viewer. Default true. */
    show?: boolean;
    /** Panel heading and button tooltip, e.g. "Building History". Default "Layers". */
    title?: string;
    /** 'multiple' (default): each layer switches on its own. 'single': picking a layer shows only it. */
    selectionMode?: 'multiple' | 'single';
    /** Offer an entry that shows every layer at once. Default true. */
    showAllOption?: boolean;
    /** Label of that entry. Default "All". */
    allLabel?: string;
    /** Open the panel when the scene starts. Default false. */
    startOpen?: boolean;
}

export interface AnnotationLayersConfig {
    enabled: boolean;
    groups: AnnotationLayerGroup[];
    shapes: AnnotationShape[];
    panel?: AnnotationLayersPanelConfig;
}

export const MAX_ANNOTATION_GROUPS = 16;
export const MAX_ANNOTATION_SHAPES = 64;
export const MAX_ANNOTATION_SHAPE_POINTS = MAX_OUTLINE_POINTS;
/**
 * Outline points across all shapes. Each one is a few lines of straight-line shader code, so this
 * bounds the shader's size and compile time (a scene with a few dozen period outlines uses ~100-200).
 */
export const MAX_ANNOTATION_TOTAL_POINTS = 512;
/**
 * Sphere / box shapes across all layers (they count toward MAX_ANNOTATION_SHAPES too). Each takes
 * a uniform slot (a mat4 and a vec4) in the splat vertex shader, which focus and haze regions
 * share, so this keeps the shader well inside WebGL2's minimum uniform budget.
 */
export const MAX_ANNOTATION_REGION_SHAPES = 8;
/** Splat object shapes across all layers (they count toward MAX_ANNOTATION_SHAPES too): one flag bit each. */
export const MAX_ANNOTATION_OBJECT_SHAPES = 8;
/** Scene coordinates beyond this are treated as corrupt (they would also print as invalid shader literals). */
export const MAX_ANNOTATION_COORDINATE = MAX_OUTLINE_COORDINATE;
export const DEFAULT_ANNOTATION_OPACITY = 0.45;
export const ANNOTATION_EFFECT_KEY = 'annotations';
export const DEFAULT_ANNOTATION_COLORS = ['#ff8a00', '#2f80ed', '#27ae60', '#eb5757', '#9b51e0', '#f2c94c', '#56ccf2', '#e84393'];

export interface ResolvedAnnotationGroup {
    id: string;
    name: string;
    color: string;
    opacity: number;
    visibleOnLoad: boolean;
    section?: string;
    showArea: boolean;
    description?: string;
    triggers?: SceneTrigger[];
    /** Sum of the layer's shape areas, scene units squared. */
    area: number;
    shapeCount: number;
}

interface ResolvedAnnotationShapeBase {
    id: string;
    groupId: string;
    groupIndex: number;
    apply: AreaApply;
}
/** An outline shape: its slab (fit, depth, feather in scene units). */
export interface ResolvedAnnotationOutline extends ResolvedAnnotationShapeBase, OutlineSlab {
    kind: 'outline';
}
/** A sphere / box shape and the uniform slot it reads its transform from. */
export interface ResolvedAnnotationVolume extends ResolvedAnnotationShapeBase {
    kind: RegionShape['shape'];
    region: RegionShape;
    slot: number;
}
/** A splat object shape: its objects and the flag lane (slot) the shader reads. */
export interface ResolvedAnnotationObjects extends ResolvedAnnotationShapeBase {
    kind: 'splats';
    ids: string[];
    slot: number;
}
export type ResolvedAnnotationShape = ResolvedAnnotationOutline | ResolvedAnnotationVolume | ResolvedAnnotationObjects;

export interface ResolvedAnnotationPanel {
    show: boolean;
    title: string;
    selectionMode: 'multiple' | 'single';
    showAllOption: boolean;
    allLabel: string;
    startOpen: boolean;
}

export interface ResolvedAnnotationLayers {
    groups: ResolvedAnnotationGroup[];
    shapes: ResolvedAnnotationShape[];
    panel: ResolvedAnnotationPanel;
}

const HEX = /^#[0-9a-f]{6}$/i;
// Typed as the config declares them; the typeof checks still reject what hand-edited JSON may hold.
const finiteOr = (value: number | undefined, fallback: number, min: number, max: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
const text = (value: string | undefined, max = 120): string | undefined =>
    typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : undefined;

/** Keep trigger entries the trigger controller can read (objects with a string `type`). */
function validTriggers(triggers: SceneTrigger[] | undefined): SceneTrigger[] | undefined {
    if (!Array.isArray(triggers)) return undefined;
    const valid = triggers.filter((t) => !!t && typeof t === 'object' && typeof (t as { type?: string }).type === 'string');
    return valid.length ? valid : undefined;
}

/** Depth and feather a new outline starts with: proportional to its outline, never thinner than its own pick noise. */
export const annotationShapeDefaults: (fit: Pick<SurfacePolygon, 'extent' | 'maxDeviation'>) => { depth: number; feather: number } = outlineSlabDefaults;

export function resolveAnnotationPanel(panel: AnnotationLayersPanelConfig | undefined): ResolvedAnnotationPanel {
    return {
        show: panel?.show !== false,
        title: text(panel?.title, 60) ?? 'Layers',
        selectionMode: panel?.selectionMode === 'single' ? 'single' : 'multiple',
        showAllOption: panel?.showAllOption !== false,
        allLabel: text(panel?.allLabel, 60) ?? 'All',
        startOpen: panel?.startOpen === true,
    };
}

/**
 * Validated, capped copy of the scene config: unknown layers, malformed colours, outlines that
 * can't be fitted (fewer than three points, all on a line), shapes of missing layers and layers
 * left without shapes drop out. Undefined when nothing would be drawn.
 */
export function resolveAnnotationLayers(config: AnnotationLayersConfig | undefined | null): ResolvedAnnotationLayers | undefined {
    if (!config || config.enabled !== true) return undefined;
    const groups: ResolvedAnnotationGroup[] = [];
    const indexById = new Map<string, number>();
    for (const g of Array.isArray(config.groups) ? config.groups : []) {
        if (groups.length >= MAX_ANNOTATION_GROUPS) break;
        if (!g || typeof g.id !== 'string' || !g.id || indexById.has(g.id)) continue;
        indexById.set(g.id, groups.length);
        groups.push({
            id: g.id,
            name: text(g.name) ?? 'Layer',
            color: typeof g.color === 'string' && HEX.test(g.color) ? g.color.toLowerCase() : DEFAULT_ANNOTATION_COLORS[groups.length % DEFAULT_ANNOTATION_COLORS.length],
            opacity: finiteOr(g.opacity, DEFAULT_ANNOTATION_OPACITY, 0, 1),
            visibleOnLoad: g.visibleOnLoad !== false,
            ...(text(g.section, 60) ? { section: text(g.section, 60) } : {}),
            showArea: g.showArea === true,
            ...(text(g.description, 200) ? { description: text(g.description, 200) } : {}),
            ...(validTriggers(g.triggers) ? { triggers: validTriggers(g.triggers) } : {}),
            area: 0,
            shapeCount: 0,
        });
    }
    if (!groups.length) return undefined;
    const shapes: ResolvedAnnotationShape[] = [];
    const seen = new Set<string>();
    let totalPoints = 0;
    let slots = 0;
    let objectSlots = 0;
    for (const s of Array.isArray(config.shapes) ? config.shapes : []) {
        if (shapes.length >= MAX_ANNOTATION_SHAPES) break;
        if (!s || typeof s.id !== 'string' || seen.has(s.id) || s.enabled === false) continue;
        const groupIndex = indexById.get(s.groupId);
        if (groupIndex === undefined) continue;
        const apply = areaApply(s.apply);
        if (s.shape === 'splats') {
            // No objects picked yet, or past the flag lanes (the editor stops adding them before this).
            const ids = normalizeSplatObjectIds(s.splatIds);
            if (!ids.length || objectSlots >= MAX_ANNOTATION_OBJECT_SHAPES) continue;
            seen.add(s.id);
            shapes.push({ id: s.id, groupId: s.groupId, groupIndex, apply, kind: 'splats', ids, slot: objectSlots++ });
            groups[groupIndex].shapeCount++;
            continue;
        }
        if (s.shape === 'sphere' || s.shape === 'box') {
            // Volumes past the uniform slots are left out (the editor stops adding them before this).
            if (slots >= MAX_ANNOTATION_REGION_SHAPES) continue;
            seen.add(s.id);
            shapes.push({ id: s.id, groupId: s.groupId, groupIndex, apply, kind: s.shape, region: normalizeRegion({ ...s, shape: s.shape }), slot: slots++ });
            groups[groupIndex].shapeCount++;
            continue;
        }
        const slab = resolveOutlineSlab(s.points, s.depth, s.feather);
        if (!slab) continue;
        // Shapes past the point budget are left out (the editor stops drawing before this).
        if (totalPoints + slab.fit.outline.length > MAX_ANNOTATION_TOTAL_POINTS) continue;
        totalPoints += slab.fit.outline.length;
        seen.add(s.id);
        shapes.push({ id: s.id, groupId: s.groupId, groupIndex, apply, kind: 'outline', ...slab });
        groups[groupIndex].area += slab.fit.area;
        groups[groupIndex].shapeCount++;
    }
    // A layer without shapes draws nothing: leave it out of the panel, the API and the selection
    // logic (a one-at-a-time panel would otherwise open on an empty period and show nothing).
    const drawn = groups.filter((g) => g.shapeCount > 0);
    if (!drawn.length) return undefined;
    const index = new Map(drawn.map((g, i) => [g.id, i] as const));
    for (const shape of shapes) shape.groupIndex = index.get(shape.groupId)!;
    return { groups: drawn, shapes, panel: resolveAnnotationPanel(config.panel) };
}

export const annotationGroupUniform = (index: number): string => `uAnnotationGroup${index}`;

/** rgb in 0..1 from #rrggbb. */
export function annotationColorRgb(hex: string): [number, number, number] {
    const valid = HEX.test(hex) ? hex : DEFAULT_ANNOTATION_COLORS[0];
    return [1, 3, 5].map((i) => parseInt(valid.slice(i, i + 2), 16) / 255) as [number, number, number];
}

/** Pack a layer's colour and its current strength (opacity × visibility fade) into its uniform. */
export function writeAnnotationGroupUniform(target: Float32Array, color: string, strength: number): Float32Array {
    const [r, g, b] = annotationColorRgb(color);
    target[0] = r;
    target[1] = g;
    target[2] = b;
    target[3] = Number.isFinite(strength) ? Math.min(1, Math.max(0, strength)) : 0;
    return target;
}

/**
 * CPU twin of the shader's per-shape weight after `apply` (tests, and "which layer is under this
 * point"). Point in runtime scene coordinates; `objectId` is the splat object the point belongs
 * to (what splat object shapes test).
 */
export function annotationShapeWeight(shape: ResolvedAnnotationShape, point: SurfacePoint, objectId?: string | null): number {
    const w =
        shape.kind === 'splats'
            ? objectId && shape.ids.includes(objectId) ? 1 : 0
            : shape.kind === 'outline'
              ? outlineSlabWeight(shape, point)
              : regionWeight(shape.region, mirrorZ(point));
    return shape.apply === 'outside' ? 1 - w : w;
}

/** CPU twin of one layer's weight in the shader: its shapes combined (combineAreaWeights). */
export function annotationLayerWeight(layers: Pick<ResolvedAnnotationLayers, 'shapes'>, groupIndex: number, point: SurfacePoint, objectId?: string | null): number {
    const members = layers.shapes.filter((s) => s.groupIndex === groupIndex);
    return members.length ? combineAreaWeights(members.map((s) => annotationShapeWeight(s, point, objectId)), members.map((s) => s.apply)) : 0;
}

/*
 * Shader layout. Every outline becomes one straight-line function with its numbers baked in as
 * literals (outline-shape.ts); every sphere / box one small function reading its uniform slot.
 * Layers are blended in order; within a layer a splat takes the combination of its shapes'
 * weights (inside shapes: the strongest, so overlapping shapes never double the tint; see
 * combineAreaShader for outside shapes).
 */

/** One outline edge as the shader tests it (all numbers in the shape's 2D outline space). */
export type AnnotationShaderEdge = OutlineShaderEdge;

/** The edges `annotationShapeWeight` walks, with the per-edge constants the shader prints. */
export const annotationShaderEdges: (shape: Pick<OutlineSlab, 'fit'>) => AnnotationShaderEdge[] = outlineShaderEdges;

/** Uniform slot a sphere / box shape reads its transform (mat4) and feather (`<name>P`.x) from. */
export const annotationShapeUniform = (slot: number): string => `uAnnotationShape${slot}`;

/**
 * One shape's weight function `name(c)` (before `apply`): an outline's slab (1 inside, 0 outside,
 * soft across `feather` and the slab faces), or a sphere / box reading its uniform slot.
 */
export function annotationShapeShader(shape: ResolvedAnnotationShape, name: string, lang: 'glsl' | 'wgsl'): string {
    if (shape.kind === 'outline') return outlineSlabShader(shape, name, lang);
    if (shape.kind === 'splats') {
        const lane = splatObjectLaneCall(ANNOTATION_EFFECT_KEY, shape.slot);
        return lang === 'glsl' ? `float ${name}(vec3 c) { return ${lane}; }` : `fn ${name}(c: vec3f) -> f32 { return ${lane}; }`;
    }
    return regionUniformShader(shape.kind, name, annotationShapeUniform(shape.slot), lang).trimEnd();
}

/** Full `region_annotations` hook for the shapes in `layers`; uniforms are named by layer index. */
export function annotationLayersShader(layers: Pick<ResolvedAnnotationLayers, 'groups' | 'shapes'>, lang: 'glsl' | 'wgsl'): string {
    const shapeFns = layers.shapes.map((s, i) => annotationShapeShader(s, `ssAnnShape${i}`, lang));
    const groupBlocks: string[] = [];
    const uniforms: string[] = [];
    layers.groups.forEach((_, gi) => {
        const members = layers.shapes.map((s, i) => (s.groupIndex === gi ? { term: applyAreaShader(`ssAnnShape${i}(center)`, s.apply), apply: s.apply } : null)).filter((x): x is { term: string; apply: AreaApply } => !!x);
        if (!members.length) return;
        const u = annotationGroupUniform(gi);
        // Inside-only layers print the `max(...)` chain they always have.
        const weight = combineAreaShader(members.map((m) => m.term), members.map((m) => m.apply));
        if (lang === 'glsl') {
            uniforms.push(`uniform vec4 ${u};`);
            groupBlocks.push(`if (${u}.a > 0.0) { float w = ${weight}; color.rgb = mix(color.rgb, ${u}.rgb, w * ${u}.a); }`);
        } else {
            uniforms.push(`uniform ${u}: vec4f;`);
            groupBlocks.push(
                `if (uniform.${u}.a > 0.0) { let w = ${weight}; *color = vec4f(mix((*color).rgb, uniform.${u}.rgb, w * uniform.${u}.a), (*color).a); }`,
            );
        }
    });
    const body = groupBlocks.join('\n    ');
    const main =
        lang === 'glsl'
            ? `void region_${ANNOTATION_EFFECT_KEY}(vec3 center, inout vec4 color) {\n    ${body}\n}`
            : `fn region_${ANNOTATION_EFFECT_KEY}(center: vec3f, color: ptr<function, vec4f>) {\n    ${body}\n}`;
    return [uniforms.join('\n'), ...shapeFns, main].join('\n');
}

/**
 * The effect to install, or undefined when nothing would be drawn. `uniforms` maps each used
 * layer's uniform name to the Float32Array the caller mutates to switch / fade / recolour it.
 */
export function buildAnnotationEffect(
    layers: ResolvedAnnotationLayers | undefined,
    uniforms: Record<string, Float32Array>,
): RegionColorEffect | undefined {
    if (!layers || !layers.shapes.length) return undefined;
    const used: Record<string, Float32Array> = {};
    for (const s of layers.shapes) {
        const name = annotationGroupUniform(s.groupIndex);
        if (!uniforms[name]) uniforms[name] = new Float32Array(4);
        used[name] = uniforms[name];
    }
    // Sphere / box transforms: written into the caller's arrays in place, so moving a shape with
    // the same shader source is a uniform change only.
    for (const s of layers.shapes) {
        if (s.kind !== 'sphere' && s.kind !== 'box') continue;
        const name = annotationShapeUniform(s.slot);
        if (!uniforms[name] || !uniforms[`${name}P`]) {
            const [slot] = createRegionUniforms(1);
            uniforms[name] = slot.matrix;
            uniforms[`${name}P`] = slot.params;
        }
        writeRegionUniform({ matrix: uniforms[name], params: uniforms[`${name}P`] }, s.region);
        used[name] = uniforms[name];
        used[`${name}P`] = uniforms[`${name}P`];
    }
    // Splat object shapes: lane = slot (splat-objects.ts).
    const objectSets: string[][] = [];
    for (const s of layers.shapes) if (s.kind === 'splats') objectSets[s.slot] = s.ids;
    return {
        key: ANNOTATION_EFFECT_KEY,
        glsl: annotationLayersShader(layers, 'glsl'),
        wgsl: annotationLayersShader(layers, 'wgsl'),
        bounds: [],
        uniforms: used,
        ...(objectSets.length ? { objectSets } : {}),
    };
}
